/**
 * 채팅 안의 **승인 카드** (2026-09-21).
 *
 * ## ★★★★ 왜 채팅에도 필요했나
 *
 * 「…에게 …라고 메일을 보내줘」라고 했더니 **묻지도 않고 나갔다.** 채팅은 비스트림 경로라
 * 모달을 못 띄웠고, 키워드 게이트로 떨어졌는데 그 게이트가 **AI 자신이 직전에 한 말**
 * (「**승인** 키워드를 포함해 다시 요청해 주세요」)을 승인으로 읽었다.
 *
 * ## AI 패널 카드와 «같은 모양»인 이유
 *
 * `AiPanel.css` 의 `.aiconfirm` 을 **그대로 쓴다**(한 줄도 안 고친다 — 가져다 쓰기만 한다).
 * 두 화면이 각자 그리면 언젠가 다르게 생기고, 「어느 화면에서 눌렀나」에 따라 사용자가 보는
 * 것이 달라진다. 승인은 그러면 안 되는 자리다.
 *
 * ## ⛔ 도구 이름·영향 범위는 **안 보여 준다** (2026-09-21 사용자 지시)
 *
 * 종전엔 카드 아래에 「`mail.send_new`  회사 밖으로 나갑니다」 한 줄이 있었다.
 * 사용자가 지우라고 했다. 근거도 맞다 — **도구 식별자 노출은 원래 금지**
 * (`SCOPE_GUARD` 의 「도구 식별자 노출 금지」)인데 이 카드만 새고 있었다.
 *
 * ★ 「되돌릴 수 없다」는 경고는 **머리글에 그대로 남는다**(`whatWillHappen` —
 *   「보낸 메일은 되돌릴 수 없습니다」). 위험 신호를 없앤 것이 아니라,
 *   **기계 이름을 치우고 사람 말만 남긴** 것이다.
 *
 * ## 패널 카드와 «다른» 점
 *
 * 미리보기 줄을 **받아 오지 않는다** — 서버가 카드를 만들 때 이미 실어 놨다. 그래서
 * 새로고침해도 그대로고, 이미 누른 카드는 닫힌 채로 다시 뜬다(단추가 없다).
 */

import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, X } from '../../../icons';
import { Button } from '../../../components/shared/Button';
import { decideChatConfirm, type ChatConfirmCard } from '../../../api/chat-ai-confirm';
/* ★ 패널 카드와 같은 모양을 쓰려고 «가져다 쓴다» — 그 파일은 고치지 않는다. */
import '../../../components/AiPanel/AiPanel.css';

interface Props {
  roomId: string;
  messageId: string;
  card: ChatConfirmCard;
}

export function AiConfirmChatCard({ roomId, messageId, card }: Props): JSX.Element {
  const [status, setStatus] = useState(card.status);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const denyRef = useRef<HTMLButtonElement>(null);

  /* ★ 되돌릴 수 없는 일이므로 초점은 «하지 않기»에 둔다 — 엔터 한 번이 실행이면 안 된다. */
  useEffect(() => { if (status === 'pending') denyRef.current?.focus(); }, [status]);

  const decide = async (decision: 'approve' | 'deny'): Promise<void> => {
    if (busy || status !== 'pending') return;
    setBusy(true);
    setError(null);
    try {
      const r = await decideChatConfirm(roomId, {
        messageId,
        continuationId: card.continuationId,
        confirmToken: card.confirmToken,
        decision,
      });
      /* 답은 방에 **AI 메시지로** 따로 남는다 — 여기서는 카드만 닫는다. */
      setStatus(r.status);
    } catch {
      setError('처리하지 못했습니다. 잠시 후 다시 시도해 주세요.');
      setBusy(false);
    }
  };

  return (
    <div className="aiconfirm" role="group" aria-label="실행 전 확인">
      <p className="aiconfirm__head">
        <AlertTriangle size={14} aria-hidden />
        실행하기 전에 확인해 주세요
      </p>
      {/* ★ 서버가 적어 준 말 그대로 — 화면이 다시 쓰지 않는다. */}
      <p className="aiconfirm__what">{card.whatWillHappen}</p>

      {card.lines.length > 0 && (
        <dl className="aiconfirm__lines">
          {card.lines.map((l) => (
            <div key={l.label} className="aiconfirm__line">
              <dt>{l.label}</dt>
              <dd>{l.value}</dd>
            </div>
          ))}
        </dl>
      )}

      {status === 'pending' ? (
        <div className="aiconfirm__acts">
          <Button
            ref={denyRef}
            size="sm"
            variant="ghost"
            iconLeft={<X size={13} />}
            disabled={busy}
            onClick={() => { void decide('deny'); }}
          >
            하지 않기
          </Button>
          <Button
            size="sm"
            variant="primary"
            iconLeft={<Check size={13} />}
            disabled={busy}
            onClick={() => { void decide('approve'); }}
          >
            {busy ? '실행 중…' : '실행하기'}
          </Button>
        </div>
      ) : (
        /* 이미 누른 카드 — 단추를 없앤다. 두 번 누를 자리를 남기지 않는다. */
        <p className="aiconfirm__loading">
          {status === 'approved' ? '실행했습니다.' : '하지 않았습니다.'}
        </p>
      )}

      {error && <p className="aiconfirm__loading">{error}</p>}
    </div>
  );
}
